/**
 * Copyright 2015 Mozilla Foundation
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { __extends } from "tslib";
import { alCoerceNumber, alDefineObjectProperties, alToNumber } from '../runtime';
import { AVM1Point, toAS3Point } from './AVM1Point';
import { Rectangle } from '@awayjs/core';
import { AVM1Object } from '../runtime/AVM1Object';
import { AVM1Function } from '../runtime/AVM1Function';
export function toAS3Rectangle(v) {
    var context = v.context;
    var x, y, width, height;
    if (v instanceof AVM1Object) {
        x = alCoerceNumber(context, v.alGet('x'));
        y = alCoerceNumber(context, v.alGet('y'));
        width = alCoerceNumber(context, v.alGet('width'));
        height = alCoerceNumber(context, v.alGet('height'));
    }
    return new Rectangle(x, y, width, height);
}
export function copyAS3RectangleTo(r, v) {
    v.alPut('x', r.x);
    v.alPut('y', r.y);
    v.alPut('width', r.width);
    v.alPut('height', r.height);
}
var AVM1Rectangle = /** @class */ (function (_super) {
    __extends(AVM1Rectangle, _super);
    function AVM1Rectangle(context, x, y, width, height) {
        var _this = _super.call(this, context) || this;
        _this.alPrototype = context.globals.Rectangle.alGetPrototypeProperty();
        _this.alPut('x', x);
        _this.alPut('y', y);
        _this.alPut('width', width);
        _this.alPut('height', height);
        return _this;
    }
    AVM1Rectangle.fromAS3Rectangle = function (context, r) {
        return new AVM1Rectangle(context, r.x, r.y, r.width, r.height);
    };
    return AVM1Rectangle;
}(AVM1Object));
export { AVM1Rectangle };
var AVM1RectangleFunction = /** @class */ (function (_super) {
    __extends(AVM1RectangleFunction, _super);
    function AVM1RectangleFunction(context) {
        var _this = _super.call(this, context) || this;
        alDefineObjectProperties(_this, {
            prototype: {
                value: new AVM1RectanglePrototype(context, _this)
            }
        });
        return _this;
    }
    AVM1RectangleFunction.prototype.alConstruct = function (args) {
        if (args && args.length > 0) {
            return new AVM1Rectangle(this.context, args[0], args[1], args[2], args[3]);
        }
        else {
            return new AVM1Rectangle(this.context, 0, 0, 0, 0);
        }
    };
    return AVM1RectangleFunction;
}(AVM1Function));
export { AVM1RectangleFunction };
var AVM1RectanglePrototype = /** @class */ (function (_super) {
    __extends(AVM1RectanglePrototype, _super);
    function AVM1RectanglePrototype(context, fn) {
        var _this = _super.call(this, context) || this;
        _this.alPrototype = context.builtins.Object.alGetPrototypeProperty();
        alDefineObjectProperties(_this, {
            constructor: {
                value: fn,
                writable: true
            },
            bottom: {
                get: _this.getBottom,
                set: _this.setBottom
            },
            bottomRight: {
                get: _this.getBottomRight,
                set: _this.setBottomRight
            },
            left: {
                get: _this.getLeft,
                set: _this.setLeft
            },
            right: {
                get: _this.getRight,
                set: _this.setRight
            },
            size: {
                get: _this.getSize,
                set: _this.setSize
            },
            top: {
                get: _this.getTop,
                set: _this.setTop
            },
            topLeft: {
                get: _this.getTopLeft,
                set: _this.setTopLeft
            },
            clone: {
                value: _this.clone,
                writable: true
            },
            contains: {
                value: _this.contains,
                writable: true
            },
            containsPoint: {
                value: _this.containsPoint,
                writable: true
            },
            containsRectangle: {
                value: _this.containsRectangle,
                writable: true
            },
            equals: {
                value: _this.equals,
                writable: true
            },
            inflate: {
                value: _this.inflate,
                writable: true
            },
            inflatePoint: {
                value: _this.inflatePoint,
                writable: true
            },
            intersection: {
                value: _this.intersection,
                writable: true
            },
            intersects: {
                value: _this.intersects,
                writable: true
            },
            isEmpty: {
                value: _this.isEmpty,
                writable: true
            },
            offset: {
                value: _this.offset,
                writable: true
            },
            offsetPoint: {
                value: _this.offsetPoint,
                writable: true
            },
            setEmpty: {
                value: _this.setEmpty,
                writable: true
            },
            toString: {
                value: _this._toString,
                writable: true
            },
            union: {
                value: _this.union,
                writable: true
            }
        });
        return _this;
    }
    AVM1RectanglePrototype.prototype.getBottom = function () {
        return alToNumber(this.context, this.alGet('y')) + alToNumber(this.context, this.alGet('height'));
    };
    AVM1RectanglePrototype.prototype.setBottom = function (value) {
        this.alPut('height', alToNumber(this.context, value) - alToNumber(this.context, this.alGet('y')));
    };
    AVM1RectanglePrototype.prototype.getBottomRight = function () {
        return new AVM1Point(this.context, AVM1RectanglePrototype.prototype.getRight.call(this), AVM1RectanglePrototype.prototype.getBottom.call(this));
    };
    AVM1RectanglePrototype.prototype.setBottomRight = function (pt) {
        AVM1RectanglePrototype.prototype.setRight.call(this, pt.alGet('x'));
        AVM1RectanglePrototype.prototype.setBottom.call(this, pt.alGet('y'));
    };
    AVM1RectanglePrototype.prototype.getLeft = function () {
        return alToNumber(this.context, this.alGet('x'));
    };
    AVM1RectanglePrototype.prototype.setLeft = function (value) {
        this.alPut('x', alToNumber(this.context, value));
    };
    AVM1RectanglePrototype.prototype.getSize = function () {
        return new AVM1Point(this.context, this.alGet('width'), this.alGet('height'));
    };
    AVM1RectanglePrototype.prototype.setSize = function (pt) {
        this.alPut('width', pt.alGet('x'));
        this.alPut('height', pt.alGet('y'));
    };
    AVM1RectanglePrototype.prototype.getRight = function () {
        return alToNumber(this.context, this.alGet('x')) + alToNumber(this.context, this.alGet('width'));
    };
    AVM1RectanglePrototype.prototype.setRight = function (value) {
        this.alPut('width', alToNumber(this.context, value) - alToNumber(this.context, this.alGet('x')));
    };
    AVM1RectanglePrototype.prototype.getTop = function () {
        return alToNumber(this.context, this.alGet('y'));
    };
    AVM1RectanglePrototype.prototype.setTop = function (value) {
        this.alPut('y', alToNumber(this.context, value));
    };
    AVM1RectanglePrototype.prototype.getTopLeft = function () {
        return new AVM1Point(this.context, AVM1RectanglePrototype.prototype.getLeft.call(this), AVM1RectanglePrototype.prototype.getTop.call(this));
    };
    AVM1RectanglePrototype.prototype.setTopLeft = function (pt) {
        AVM1RectanglePrototype.prototype.setLeft.call(this, pt.alGet('x'));
        AVM1RectanglePrototype.prototype.setTop.call(this, pt.alGet('y'));
    };
    AVM1RectanglePrototype.prototype.clone = function () {
        var result = new AVM1Rectangle(this.context);
        if (this instanceof AVM1Object) {
            result.alPut('x', this.alGet('x'));
            result.alPut('y', this.alGet('y'));
            result.alPut('width', this.alGet('width'));
            result.alPut('height', this.alGet('height'));
        }
        return result;
    };
    AVM1RectanglePrototype.prototype.contains = function (x, y) {
        x = alToNumber(this.context, x);
        y = alToNumber(this.context, y);
        var r = toAS3Rectangle(this);
        return r.contains(x, y);
    };
    AVM1RectanglePrototype.prototype.containsPoint = function (pt) {
        var r = toAS3Rectangle(this), p = toAS3Point(pt);
        return r.containsPoint(p);
    };
    AVM1RectanglePrototype.prototype.containsRectangle = function (rect) {
        var r = toAS3Rectangle(this), other = toAS3Rectangle(rect);
        return r.containsRect(other);
    };
    AVM1RectanglePrototype.prototype.equals = function (toCompare) {
        var r = toAS3Rectangle(this), other = toAS3Rectangle(toCompare);
        return r.equals(other);
    };
    AVM1RectanglePrototype.prototype.inflate = function (dx, dy) {
        dx = alToNumber(this.context, dx);
        dy = alToNumber(this.context, dy);
        var r = toAS3Rectangle(this);
        r.inflate(dx, dy);
        copyAS3RectangleTo(r, this);
    };
    AVM1RectanglePrototype.prototype.inflatePoint = function (pt) {
        var r = toAS3Rectangle(this), p = toAS3Point(pt);
        r.inflatePoint(p);
        copyAS3RectangleTo(r, this);
    };
    AVM1RectanglePrototype.prototype.intersection = function (toIntersect) {
        var r = toAS3Rectangle(this), other = toAS3Rectangle(toIntersect);
        return AVM1Rectangle.fromAS3Rectangle(this.context, r.intersection(other));
    };
    AVM1RectanglePrototype.prototype.intersects = function (toIntersect) {
        var r = toAS3Rectangle(this), other = toAS3Rectangle(toIntersect);
        return r.intersects(other);
    };
    AVM1RectanglePrototype.prototype.isEmpty = function () {
        return toAS3Rectangle(this).isEmpty();
    };
    AVM1RectanglePrototype.prototype.offset = function (dx, dy) {
        dx = alToNumber(this.context, dx);
        dy = alToNumber(this.context, dy);
        var r = toAS3Rectangle(this);
        r.offset(dx, dy);
        copyAS3RectangleTo(r, this);
    };
    AVM1RectanglePrototype.prototype.offsetPoint = function (pt) {
        var r = toAS3Rectangle(this), p = toAS3Point(pt);
        r.offsetPoint(p);
        copyAS3RectangleTo(r, this);
    };
    AVM1RectanglePrototype.prototype.setEmpty = function () {
        this.alPut('x', 0);
        this.alPut('y', 0);
        this.alPut('width', 0);
        this.alPut('height', 0);
    };
    AVM1RectanglePrototype.prototype._toString = function () {
        return '(x=' + this.alGet('x') + ', y=' + this.alGet('y') +
            ', w=' + this.alGet('width') + ', h=' + this.alGet('height') + ')';
    };
    AVM1RectanglePrototype.prototype.union = function (toUnion) {
        var r = toAS3Rectangle(this), other = toAS3Rectangle(toUnion);
        return AVM1Rectangle.fromAS3Rectangle(this.context, r.union(other));
    };
    return AVM1RectanglePrototype;
}(AVM1Object));
export { AVM1RectanglePrototype };
